import React, {
  forwardRef,
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { utils as ejsUtils } from '@tangibleplay/ecommerce-js';
import {
  Select as SelectInput,
  SelectOption,
  SelectProps,
} from '@tangibleplay/core-ui';

import { StyledAddressForm } from './AddressForm.styles';

import { getRegions, validateAddressFields } from './utils';
import { fieldNames, getFieldsConfig } from './AddressForm.config';
import {
  AddressFormProps,
  CountryType,
  FieldNamesType,
} from './AddressForm.types';
import DefaultInputForForms from '../../_utils/DefaultInputForForms';

/**
 * ------------------------------------
 * CONSTANTS
 * ------------------------------------
 */

/**
 * Constants for Gmaps autocomplete
 */
const countriesRegionsAsLevel2 = ['GB'];
// Fields that can be assigned without any specific transformation or action
const fieldNamesByAddressTypes: { [key: string]: string } = {
  postal_town: 'city',
  locality: 'city',
  postal_code: 'zip',
  postal_code_prefix: 'zip',
  country: 'country',
};
// Map which format we need to take for each address component
type ComponentFormValuesType = 'short_name' | 'long_name';
const componentForm: { [key: string]: ComponentFormValuesType } = {
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  postal_town: 'long_name',
  administrative_area_level_1: 'short_name',
  administrative_area_level_2: 'short_name',
  country: 'short_name',
  postal_code: 'short_name',
  postal_code_prefix: 'short_name',
};

/**
 * ------------------------------------
 * COMPONENT
 * ------------------------------------
 */
//TODO Remove Duplicate "customClassName" prop (duplicate of "customClassnames")
const AddressForm = forwardRef(
  (
    {
      // Events
      onChange = () => {},
      onFieldChange = () => {},
      // Fields data
      values,
      labels,
      placeholders,
      errors,
      fieldsExtraProps = {},
      countriesList,
      // Tools
      prefixAutoComplete = (a: string) => a,
      requiredFields = [
        fieldNames.FIRST_NAME,
        fieldNames.LAST_NAME,
        fieldNames.ADDRESS_1,
        fieldNames.CITY,
        fieldNames.ZIPCODE,
        fieldNames.STATE,
        fieldNames.COUNTRY,
      ],
      disabledFields = [],
      fieldsToHide = [],
      hasCountriesCharactersRestrictions = false,
      areGmapsSuggestionsOn = false,
      // Components
      inputComponent: Field = DefaultInputForForms,
      selectComponent: Select = SelectInput,
      // Styles
      customClassName = '',
      customFieldWrapperClassName = '',
      componentClassname = 'address-form',
      customClassnames = '',
      customStyle = '',
    }: AddressFormProps,
    ref,
  ) => {
    /**
     * Constants
     */

    const fieldsConfig = getFieldsConfig(values[fieldNames.COUNTRY]);
    const addressInputRef = useRef<HTMLInputElement>(null);
    const zipInputRef = useRef<HTMLInputElement>(null);
    let autocomplete: google.maps.places.Autocomplete;
    const hideField = (fieldName: FieldNamesType) =>
      fieldsToHide.includes(fieldName);
    const regions = getRegions(values[fieldNames.COUNTRY]);
    const regionOptions =
      regions &&
      regions.map((region) => ({
        label: region.name,
        value: region.id,
      }));

    /**
     * Hooks
     */
    useEffect(() => {
      if (areGmapsSuggestionsOn && values[fieldNames.COUNTRY] === 'US') {
        initializeAutocomplete();
      } else {
        removeAutocomplete();
      }

      return () => {
        removeAutocomplete();
      };
    }, [
      areGmapsSuggestionsOn,
      values[fieldNames.COUNTRY],
      // We need to add values to the array dependencies otherwise the value of `values` in `populateFields` wont be up to date
      // We can't add all the values, otherwise it'll remove the autocomplete while typing in the address1 field
      values[fieldNames.FIRST_NAME],
      values[fieldNames.LAST_NAME],
      values[fieldNames.ADDRESS_2],
      values[fieldNames.ADDRESS_3],
    ]);

    useImperativeHandle(ref, () => ({
      validateFields() {
        const errors = validateAddressFields(
          values,
          hasCountriesCharactersRestrictions,
          requiredFields,
        );

        return errors;
      },
    }));

    /**
     * Event handlers
     */

    const handleTextChange = (fieldName: FieldNamesType, value: string) => {
      onFieldChange(fieldName, value);

      handleFieldChange(fieldName, value);
    };

    const handleSelectChange = (
      fieldName: FieldNamesType,
      option: SelectOption,
    ) => {
      const { value } = option;

      onFieldChange(fieldName, value);

      handleFieldChange(fieldName, value);
    };

    const handleFieldChange = (fieldName: FieldNamesType, value: string) => {
      let additionalUpdates: AddressFormProps['values'] = {};

      if (fieldName === fieldNames.COUNTRY) {
        additionalUpdates.state = '';
      }

      handleChange(
        {
          ...values,
          ...additionalUpdates,
          [fieldName]: value,
        },
        'single-input-update',
      );

      if (fieldName === fieldNames.ADDRESS_1 && areGmapsSuggestionsOn) {
        switchAddressInputAutocompletion();
      } else if (fieldName === fieldNames.ZIPCODE && areGmapsSuggestionsOn) {
        toggleAutocompletionInZipCodeInputField();
      }
    };

    const handleChange = (
      values: AddressFormProps['values'],
      source?: string,
    ) => {
      onChange(values, source);
    };

    /**
     * Gmaps autocomplete methods
     * The way we handle Gmaps places autocomplete API is inspired by this example from the documentation
     * https://developers-dot-devsite-v2-prod.appspot.com/maps/documentation/javascript/examples/places-autocomplete-addressform
     */
    const initializeAutocomplete = () => {
      const addressInput = addressInputRef.current;

      addressInput.addEventListener('focus', switchAddressInputAutocompletion);

      if (typeof google !== 'undefined' && google) {
        autocomplete = new google.maps.places.Autocomplete(addressInput, {
          types: ['geocode'],
          strictBounds: true,
          componentRestrictions: { country: 'US' },
        });
        autocomplete.setFields(['address_components']);
        autocomplete.addListener('place_changed', () => fillInAddress());
      }
    };

    const removeAutocomplete = () => {
      const addressInput = addressInputRef.current;
      if (typeof google !== 'undefined' && google && addressInput) {
        addressInput.removeEventListener(
          'focus',
          switchAddressInputAutocompletion,
        );
        google.maps.event.clearInstanceListeners(addressInput);
      }
    };

    const fillInAddress = () => {
      const place = autocomplete.getPlace();

      // Sometimes for example when internet is disconnected and customer selects an address from
      // autocomplete dropdown the value comes under place.name and place.address_components gets undefined
      // Hence check availablity of both place and place.address_components
      if (place && place.address_components) {
        const countryAddress = place.address_components.find(
          (address) => address.types[0] === fieldNames.COUNTRY,
        );
        const country = countryAddress.short_name;
        populateFields(place, country);
      }
    };

    const populateFields = (
      place: google.maps.places.PlaceResult,
      country: CountryType,
    ) => {
      let address = ' '; // we'll need to assign the street number and route name to this later
      const newValues: { [key: string]: string } = { ...values };
      for (const addressComponent of place.address_components) {
        const addressType = addressComponent.types[0];
        if (componentForm[addressType]) {
          const val = addressComponent[componentForm[addressType]];
          if (addressType === 'street_number') {
            address = `${val}${address}`;
          }
          if (addressType === 'route') {
            address = `${address}${val}`;
          }
          if (
            (addressType === 'administrative_area_level_2' &&
              countriesRegionsAsLevel2.findIndex((c) => c === country) !==
                -1) ||
            (addressType === 'administrative_area_level_1' &&
              countriesRegionsAsLevel2.findIndex((c) => c === country) === -1)
          ) {
            newValues[fieldNames.STATE] = val;
            // Update fields that don't need any particular transformation
          } else if (fieldNamesByAddressTypes[addressType]) {
            newValues[fieldNamesByAddressTypes[addressType]] = val;
          }
        }
      }
      // Finally, the address should be formatted so we can set it
      if (address.replace(/ /g, '').length) {
        newValues[fieldNames.ADDRESS_1] = address;
        // this.validateAddress(fieldNames.ADDRESS_1, address); //TODO
      } else {
        // else, we set it empty so we can still see the placeholder
        newValues[fieldNames.ADDRESS_1] = '';
      }

      handleChange({ ...values, ...newValues }, 'suggestion-selection');
    };

    // We need to change the autocomplete attribute programatically as Gmaps overrides it to "off"
    // And with autocomplete="off" Chrome still shows browser autocompletion for addresses
    // https://stackoverflow.com/questions/29931712/chrome-autofill-covers-autocomplete-for-google-maps-api-v3
    const switchAddressInputAutocompletion = () => {
      const input = addressInputRef.current;

      // disable the browser autocompletion only when the user starts writing something in the input
      // (so they can still see their addresses stored into their browser before they type a letter and we suggest addresses from gmaps)
      if (input && input.value.length >= 1) {
        input.setAttribute('autocomplete', 'new-password');
      } else {
        input.setAttribute('autocomplete', 'address-line1');
      }
    };

    const toggleAutocompletionInZipCodeInputField = () => {
      const input = zipInputRef.current;

      // Disable the browser autocompletion only when the user starts writing something in the input
      // (so they can still see their addresses stored into their browser before they type a letter and we suggest addresses from gmaps)
      if (input && input.value.length >= 1) {
        input.setAttribute('autocomplete', 'new-password');
      } else {
        input.setAttribute('autocomplete', 'postal-code');
      }
    };

    /**
     * Render helper functions
     */
    const renderTextField = (
      fieldName: FieldNamesType,
      ref?: MutableRefObject<any>,
    ) => {
      const otherProps =
        (fieldsExtraProps && fieldsExtraProps[fieldName]) || {};

      return (
        <Field
          ref={ref}
          name={fieldsConfig[fieldName].name}
          placeholder={
            (placeholders && placeholders[fieldName]) ||
            fieldsConfig[fieldName].defaultPlaceholder
          }
          value={values[fieldName]}
          error={errors ? errors[fieldName] : undefined}
          onChange={(value: string) => handleTextChange(fieldName, value)}
          autoComplete={prefixAutoComplete(
            fieldsConfig[fieldName].autoComplete,
          )}
          maxLength={fieldsConfig[fieldName].maxLength}
          label={
            (labels && labels[fieldName]) ||
            fieldsConfig[fieldName].defaultLabel
          }
          required={requiredFields.includes(fieldName)}
          disabled={disabledFields.includes(fieldName)}
          format={fieldsConfig[fieldName].format}
          parse={fieldsConfig[fieldName].parse}
          {...otherProps}
        />
      );
    };

    const renderSelectField = (
      fieldName: FieldNamesType,
      options: AddressFormProps['countriesList'],
      defaultValue?: SelectProps['defaultValue'],
    ) => {
      const otherProps =
        (fieldsExtraProps && fieldsExtraProps[fieldName]) || {};

      const valueKey = values[fieldName];
      const value =
        options.find((option) => option.value === valueKey) || defaultValue;

      return (
        <Select
          name={fieldsConfig[fieldName].name}
          placeholder={
            (placeholders && placeholders[fieldName]) ||
            fieldsConfig[fieldName].defaultPlaceholder
          }
          defaultValue={defaultValue}
          value={value}
          error={errors ? errors[fieldName] : undefined}
          onChange={(value: SelectOption) =>
            handleSelectChange(fieldName, value)
          }
          autoComplete={prefixAutoComplete(
            fieldsConfig[fieldName].autoComplete,
          )}
          label={
            (labels && labels[fieldName]) ||
            fieldsConfig[fieldName].defaultLabel
          }
          options={options}
          required={requiredFields.includes(fieldName)}
          {...otherProps}
        />
      );
    };

    const getFieldWrapperClasses = (fieldName: string) =>
      `${componentClassname}__field-wrapper ${componentClassname}__field-wrapper--${fieldName} ${
        customFieldWrapperClassName?.length
          ? `${customFieldWrapperClassName} ${customFieldWrapperClassName}--${fieldName}`
          : ''
      }`;

    /**
     * Render
     */
    return (
      <StyledAddressForm
        className={`${componentClassname} ${customClassnames} ${customClassName}`}
        customStyle={customStyle}
      >
        {!hideField(fieldNames.FIRST_NAME) && (
          <div className={getFieldWrapperClasses(fieldNames.FIRST_NAME)}>
            {renderTextField(fieldNames.FIRST_NAME)}
          </div>
        )}
        {!hideField(fieldNames.LAST_NAME) && (
          <div className={getFieldWrapperClasses(fieldNames.LAST_NAME)}>
            {renderTextField(fieldNames.LAST_NAME)}
          </div>
        )}
        {!hideField(fieldNames.ADDRESS_1) && (
          <div className={getFieldWrapperClasses(fieldNames.ADDRESS_1)}>
            {renderTextField(fieldNames.ADDRESS_1, addressInputRef)}
          </div>
        )}
        {!hideField(fieldNames.ADDRESS_2) && (
          <div className={getFieldWrapperClasses(fieldNames.ADDRESS_2)}>
            {renderTextField(fieldNames.ADDRESS_2)}
          </div>
        )}
        {!hideField(fieldNames.CITY) && (
          <div className={getFieldWrapperClasses(fieldNames.CITY)}>
            {renderTextField(fieldNames.CITY)}
          </div>
        )}
        {!hideField(fieldNames.STATE) && (
          <div className={getFieldWrapperClasses(fieldNames.STATE)}>
            {regionOptions
              ? renderSelectField(fieldNames.STATE, regionOptions)
              : renderTextField(fieldNames.STATE)}
          </div>
        )}
        {!hideField(fieldNames.ZIPCODE) && (
          <div className={getFieldWrapperClasses(fieldNames.ZIPCODE)}>
            {renderTextField(fieldNames.ZIPCODE, zipInputRef)}
          </div>
        )}
        {!hideField(fieldNames.ADDRESS_3) && (
          <div className={getFieldWrapperClasses(fieldNames.ADDRESS_3)}>
            {renderTextField(fieldNames.ADDRESS_3)}
          </div>
        )}
        {!hideField(fieldNames.COUNTRY) && countriesList && (
          <div className={getFieldWrapperClasses(fieldNames.COUNTRY)}>
            {renderSelectField(
              fieldNames.COUNTRY,
              countriesList,
              countriesList.find((option) => option.value === values.country),
            )}
          </div>
        )}
      </StyledAddressForm>
    );
  },
);
AddressForm.displayName = 'AddressForm';

export default AddressForm;
